'use client';

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { bff } from '@/lib/waitlist/client';
import { emailRequestSchema, fieldErrors } from '@/lib/waitlist/schema';
import { Honeypot, LiveMessage, errorClass, inputClass, labelClass, primaryButtonClass, waitCopy } from './ui';

/** The gateway sends at most one email of a kind every 2 minutes per address. */
export const RESEND_COOLDOWN_MS = 120_000;

const GENERIC: Record<'resend' | 'manage-link', string> = {
  resend:
    "If that address is waiting to be confirmed, we've sent a new link. It can take a few minutes to arrive, so check your spam folder too. You can ask again in 2 minutes.",
  'manage-link':
    "If that address is on the waitlist, we've sent it a secure link. It works once and expires after 24 hours. Check your spam folder too. You can ask again in 2 minutes.",
};

/**
 * One email field and a button: "send me a new confirmation email" or "send
 * me a secure link". The answer is the same whether or not the address is
 * known, so the form cannot be used to find out who is on the list.
 */
export default function EmailRequestForm({
  kind,
  submitLabel,
  defaultEmail = '',
}: {
  kind: 'resend' | 'manage-link';
  submitLabel: string;
  defaultEmail?: string;
}) {
  const id = useId();
  const [email, setEmail] = useState(defaultEmail);
  const [website, setWebsite] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [state, setState] = useState<{ kind: 'idle' | 'busy' | 'sent' } | { kind: 'failed'; message: string }>({ kind: 'idle' });
  const [coolingDown, setCoolingDown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind === 'busy' || coolingDown) return;
    const parsed = emailRequestSchema.safeParse({ email, website: website || undefined });
    if (!parsed.success) {
      setError(fieldErrors(parsed.error).email ?? 'Enter your email address.');
      inputRef.current?.focus();
      return;
    }
    setError(undefined);
    setState({ kind: 'busy' });
    const res = await bff<{ accepted: boolean }>(`/${kind}`, { body: parsed.data });
    if (res.ok) {
      setState({ kind: 'sent' });
      setCoolingDown(true);
      timer.current = setTimeout(() => setCoolingDown(false), RESEND_COOLDOWN_MS);
    } else if (res.code === 'invalid' && res.fields?.email) {
      setState({ kind: 'idle' });
      setError(res.fields.email);
      inputRef.current?.focus();
      return;
    } else if (res.status === 429) {
      setState({ kind: 'failed', message: `Too many attempts. Please wait ${waitCopy(res.retryAfter)} and try again.` });
    } else {
      setState({ kind: 'failed', message: res.error });
    }
    requestAnimationFrame(() => messageRef.current?.focus());
  }

  return (
    <form onSubmit={onSubmit} noValidate className="relative flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label htmlFor={`${id}-email`} className={labelClass}>
          Email address
        </label>
        <input
          ref={inputRef}
          id={`${id}-email`}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-email-error` : undefined}
          className={inputClass}
        />
        {error ? (
          <p id={`${id}-email-error`} className={errorClass}>
            {error}
          </p>
        ) : null}
      </div>
      <Honeypot id={`${id}-website`} value={website} onChange={setWebsite} />
      <button type="submit" disabled={state.kind === 'busy' || coolingDown} className={`${primaryButtonClass} w-full sm:w-auto sm:self-start`}>
        {state.kind === 'busy' ? 'Sending…' : submitLabel}
      </button>
      <LiveMessage ref={messageRef} tone={state.kind === 'failed' ? 'error' : 'success'}>
        {state.kind === 'sent' ? GENERIC[kind] : state.kind === 'failed' ? state.message : null}
      </LiveMessage>
    </form>
  );
}
